/* eslint-disable react-hooks/exhaustive-deps */
import { PropsWithChildren, useEffect, useRef } from 'react';
import { Box } from '@cp/ds/src/components/box';
import { IToastItem, ToastIdentifier } from './types';
import { TOAST_STEPS } from './const';

interface ToastStepsProps extends PropsWithChildren {
  toast: IToastItem;
  onDelete: (toastId: ToastIdentifier) => void;
  onOpened: (toast: IToastItem) => void;
  onMouseOver: () => void;
  onMouseLeave: () => void;
}

export const ToastSteps = ({ toast, children, onDelete, onOpened, onMouseLeave, onMouseOver }: ToastStepsProps) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!parentRef.current || !wrapRef.current) return;
    parentRef.current.style.height = wrapRef.current.offsetHeight + 14 + 'px';

    const ac = new AbortController();
    parentRef.current.addEventListener('mouseover', () => onMouseOver(), { signal: ac.signal });
    parentRef.current.addEventListener('mouseleave', () => onMouseLeave(), { signal: ac.signal });
    return () => ac.abort();
  }, []);

  useEffect(() => {
    if (!wrapRef.current || !parentRef.current) return;

    const ac = new AbortController();
    const setWrapAnimation = () => {
      if (wrapRef.current) {
        wrapRef.current.style.animation = `toast-${toast.step}-${toast.animation} 0.2s ease-in-out forwards`;
      }
    };

    if (toast.step === TOAST_STEPS.INIT) {
      const initWrapHandle = () => onOpened(toast);
      wrapRef.current.addEventListener('animationend', initWrapHandle, { once: true, signal: ac.signal });
      parentRef.current?.addEventListener('transitionend', setWrapAnimation, { once: true, signal: ac.signal });
    }

    if (toast.step === TOAST_STEPS.CLOSE) {
      const closeWrapHandle = () => {
        if (!parentRef.current) return;
        parentRef.current.style.height = '0px';
      };
      const closeParentHandle = () => onDelete(toast.id);
      wrapRef.current.addEventListener('animationend', closeWrapHandle, { once: true, signal: ac.signal });
      parentRef.current.addEventListener('transitionend', closeParentHandle, { once: true, signal: ac.signal });
      setWrapAnimation();
    }

    return () => ac.abort();
  }, [toast.step]);

  return (
    <Box ref={parentRef} sx={{ height: 0, transition: '0.2s ease-in-out' }}>
      <Box ref={wrapRef} sx={{ opacity: toast.step === TOAST_STEPS.INIT ? 0 : 1 }}>
        {children}
      </Box>
    </Box>
  );
};
